import React, { useCallback, useEffect, useState } from "react";
import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { StatusEnum } from "../../../hoc/with-status";
import { US_CAAS_STATUS } from "../i9-data";
import useArb from "../../../hooks/use-arb";
import { I9Response, I9TaskCardProps } from "../../../utility/types/common";
import { useParams } from "react-router-dom";
import { getTaskDefinitionsFromConfig } from "../../../utility/app-config-helper";
import { RIGHT_TO_WORK_TASK_DEFINITION_ID, RIGHT_TO_WORK_TASK_TITLE } from "../../../config/appConfigConstants";
import { getCandidateData } from "../../../utility/candidate-data";
import { getCaasInfo } from "../../../service/get-caas-info";
import { isDragonstone } from "../../../helpers/url-params";
import { addPageLoadMetric } from "../../../utility/adobe-analytics";
import { I9_ADVANTAGE_URL } from "../../../config/urls";

export const I9TaskCardButton = ({ caasPath, status, shouldOverrideStatus }: I9TaskCardProps) => {
  if (!status) {
    return null;
  }
  const overrideStatus = shouldOverrideStatus ? shouldOverrideStatus[status] : status;

  const [isI9ErrorCode, setIsI9ErrorCode] = useState<boolean>(false);
  const [i9Response, setI9Response] = useState<I9Response>({});

  const bundle = useArb("HVHChecklistPantherResource");
  const translatedI9Button = bundle.getMessage("Checklist-MainPage-I9Legacy-ButtonText");
  const translatedRtwButton = bundle.getMessage("Checklist-MainPage-RightToWork-ButtonText");

  const { applicationId, requisitionId } = useParams<{
    applicationId: string;
    requisitionId: string;
  }>();

  const resolveButtonText = (): string => {
    const taskDefinition = getTaskDefinitionsFromConfig(RIGHT_TO_WORK_TASK_DEFINITION_ID, applicationId);

    if (taskDefinition?.taskTitle == RIGHT_TO_WORK_TASK_TITLE) return translatedRtwButton;

    return translatedI9Button;
  };

  const i9LinkAccessor = useCallback(async () => {
    const { candidateId } = await getCandidateData(applicationId);
    return getCaasInfo(candidateId, caasPath);
  }, [applicationId]);

  const trackI9LinkClick = useCallback(() => {
    addPageLoadMetric(window, applicationId, "i9advantage", {
      jobId: requisitionId,
      isDragonstone: isDragonstone(requisitionId),
    });
  }, [applicationId, requisitionId]);

  const requestI9Link = useCallback(async () => {
    try {
      const response = await i9LinkAccessor();
      setI9Response(response);
      if (response.errorCode) {
        setIsI9ErrorCode(true);
      } else {
        setIsI9ErrorCode(false);
      }
    } catch (e) {
      console.log(e);
    }
  }, [i9LinkAccessor]);

  useEffect(() => {
    requestI9Link();
  }, []);

  const redirect = useCallback(() => {
    if (!i9Response?.secureURL) {
      trackI9LinkClick();
      window.open(I9_ADVANTAGE_URL, "_blank");
      return;
    }
    trackI9LinkClick();

    window.open(i9Response?.secureURL, "_blank");
  }, [i9Response?.secureURL, trackI9LinkClick]);

  const shouldDisableButton = (): boolean => {
    if (
      overrideStatus !== StatusEnum.ACTION_REQUIRED ||
      i9Response?.status?.toString() !== US_CAAS_STATUS.PendingSection1.toString() ||
      isI9ErrorCode
    ) {
      return true;
    }

    return false;
  };

  return (
    <Button variant={ButtonVariant.Primary} onClick={redirect} disabled={shouldDisableButton()}>
      {resolveButtonText()}
    </Button>
  );
};
